import { Component, type OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';

import { DashboardService } from '../../../../service/events/dashboard.service';
import { ClientesApiService } from '../../../../service/api/clientes-api.service';
import { UsuariosApiService } from '../../../../service/api/usuarios-api.service';
import { UploadApiService } from '../../../../service/api/upload-api.service';
import { RopoApiService } from '../../../../service/api/ropo-api.service';

import { BaseForm } from '../../../base-form';
import { build, stack } from '../../../../common/classes/request-builder';
import { EventPhase, FormPhase } from '../../../../common/components/reactive-form-builder/form-request';
import { ReactiveFormBuilderComponent } from '../../../../common/components/reactive-form-builder/form-builder.component';

import { environment } from '../../../../../environments/environment';

import { ClienteModel } from '../../../../models/cliente.model';
import { UserModel } from '../../../../models/usuarios/user.model';
import { ClientesFormDefinition } from './form-definition';
import { ClientesEnvironmentDefinition } from '../environment-variables';
import { nextSpanishIdCharPattern } from '../../../../common/components/reactive-form-builder/utils/spanish-id.keyfilter';

@Component({
  selector: 'app-clientes-form',
  standalone: false,
  templateUrl: './clientes-form.component.html',
  styleUrls: ['./clientes-form.component.scss']
})
export class ClientesFormComponent extends BaseForm<ClienteModel> implements OnInit, OnDestroy {  
  @ViewChild(ReactiveFormBuilderComponent) formBuilder!: ReactiveFormBuilderComponent<ClienteModel>;
  public dropdownsFinalized = false;

  /** Reactive form definition bound to the template */
  public form!: ClientesFormDefinition;

  /** Form request engine (phase-based hooks) */
  public readonly formRequest!: ReactiveFormBuilderComponent<ClienteModel>;

  /** Environment flags for feature toggles and labels */
  public readonly env: ClientesEnvironmentDefinition = new ClientesEnvironmentDefinition();

  /** Emits when dropdowns are ready to be reloaded */
  public readonly dropdownLoadTrigger = new Subject<void>();

  constructor(
    public override readonly router: Router,
    public override readonly route: ActivatedRoute,
    public override readonly dashboard: DashboardService,
    public readonly usuariosApi: UsuariosApiService,
    public readonly clientesApi: ClientesApiService,
    public readonly _ropoService: RopoApiService,
    private readonly http: HttpClient,
    public readonly uploadApi: UploadApiService
  ) {
    // BaseForm wires up PUT/POST handlers and breadcrumbs/titles
    super(
      route,
      router,
      dashboard,
      clientesApi.clientesPUT,
      clientesApi.clientesPOST,
      'clientes',
      'Editar ' + environment.features.clienteName,
      'Crear ' + environment.features.clienteName,
      'Duplicar ' + environment.features.clienteName
    );
    this.formRequest = new ReactiveFormBuilderComponent(this.http);
  }

  /** Initialize form definition, load lists, and prepare the model */
  ngOnInit(): void {
    this.dropdownsFinalized = false;
    const type = this.getType();
    const baseModel = this.getModel() as Record<string, string>;
    
    this.form = new ClientesFormDefinition(type, baseModel, null);
    this.expandFormRequest();

    // Phase: LOAD -> fetch lists before rendering dependent fields
    this.formRequest.executePhase(FormPhase.LOAD);

    // BaseForm initialization helpers (keeps navigation state, etc.)
    this.softInit(type);
  }

  /**
   * Handle form-level changes coming from the builder.
   * - Keeps the form model in sync
   * - Runs field-level validations by `tag`
   * - Optionally tries to auto-fill REGEPA from NIF
   */
  public formChanges(formModel: object, tag: string): void {
    this.form.model = formModel as ClienteModel;

    /** Minimum valid NIF length used to trigger REGEPA autofill */
    const NIF_LENGTH = 9;

    if (tag === 'nif') {      
      this.form.spanishIdKeyPattern = nextSpanishIdCharPattern(this.form.model.nif ?? '');
    }
    
    // Auto-calc REGEPA on NIF input (if feature is enabled)
    if (
      tag === 'nif' &&
      this.env.showRopoRomaRegepa &&
      (this.form.model.nif ?? '').length >= NIF_LENGTH
    ) {
      void this.handleRegepaAsync(this.form.model.nif ?? '');
    }
  }


  /**
   * Enrich the phase engine with data-loading and pre-send validations.
   * LOAD: fetch usuarios + clientes, then notify dropdowns
   */
  private expandFormRequest(): void {
    // NOTE: do not change pool/stack behavior here (performance-sensitive path)
    this.formRequest.addEvent(FormPhase.LOAD, EventPhase.BEFORE, (resolve) => {
      stack([
        build(
          this.usuariosApi.usuariosGET,
          this.form.lists.usuarios,
          (it: UserModel) => `${it.nombre || ''} ${it.apellidos || ''}`,
          true
        ),
        build(this.clientesApi.clientesGET, this.form.lists.clientes, 'nombre', true)
      ])
        .then(() => {
          this.dropdownsFinalized = true;
          this.dropdownLoadTrigger.next();
          resolve(true);
        })
        .catch(e => {
          console.error('Error loading lists (LOAD phase):', e);
          this.dropdownsFinalized = true;
          resolve(true);
        });
    });
  }

  /**
   * Try to fetch REGEPA info by NIF and update the form model accordingly.
   * Isolated as async to keep formChanges clean and testable.
   */
  private async handleRegepaAsync(nif: string): Promise<void> {
    try {
      const resp = await firstValueFrom(this._ropoService.getREGEPA(nif));
      const regepa = (resp as any)?.regepa;

      if ((Boolean(regepa)) && Object.keys(regepa).length > 0) {
        this.form.model.regepa = regepa['código'];
        this.formBuilder.updateForm(this.form.model, false);
      }
    } catch (error) {
      console.error('REGEPA lookup failed:', error);
    }
  }
}
